import {
  Box,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import { collection, orderBy, query } from 'firebase/firestore'
import { useCollection } from 'react-firebase-hooks/firestore'
import { useAuth } from 'src/contexts/AuthContext'
import { firestore } from 'src/shims/firebase'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ReceiptIcon from '@mui/icons-material/Receipt'

type InvoiceRow = {
  id?: string
  code?: string
  status?: string
  amount_paid?: number
  currency?: string
  period_start?: number
  period_end?: number
  hosted_invoice_url?: string
  invoice_pdf?: string
}

export function BillsPage() {
  const { user } = useAuth()
  const uid = user?.uid ?? ''

  const invoicesRef = collection(firestore, 'subscription', uid, 'invoices')
  const q = query(invoicesRef, orderBy('period_start', 'desc'))
  const [snapshot, loading, error] = useCollection(q)
  const invoices: InvoiceRow[] | undefined = snapshot?.docs.map((d) => ({
    id: d.id,
    ...d.data()
  })) as InvoiceRow[]

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <ReceiptIcon sx={{ fontSize: 32, color: '#4277b9' }} />
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#2c3e50' }}>
          Mes factures
        </Typography>
      </Box>

      {loading ? (
        <Typography sx={{ color: '#7f8c8d' }}>Chargement…</Typography>
      ) : error ? (
        <Typography color="error">Erreur lors du chargement des factures.</Typography>
      ) : !invoices || invoices.length === 0 ? (
        <Paper
          sx={{
            p: 5,
            textAlign: 'center',
            borderRadius: 3,
            border: '2px dashed rgba(0,0,0,0.1)',
            backgroundColor: 'rgba(66,119,185,0.02)'
          }}>
          <ReceiptIcon sx={{ fontSize: 48, color: '#bdc3c7', mb: 2 }} />
          <Typography sx={{ color: '#7f8c8d', fontSize: '1.1rem' }}>
            Aucune facture pour le moment.
          </Typography>
          <Typography sx={{ color: '#95a5a6', mt: 1, fontSize: '0.95rem' }}>
            Vos factures apparaîtront ici après chaque paiement d&apos;abonnement.
          </Typography>
        </Paper>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: 3,
            border: '1px solid rgba(0,0,0,0.06)',
            boxShadow: '0 2px 12px rgba(0,0,0,0.04)'
          }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'rgba(66,119,185,0.04)' }}>
                <TableCell sx={{ fontWeight: 700, color: '#2c3e50' }}>Statut</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#2c3e50' }}>Période</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#2c3e50' }} align="right">
                  Montant
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#2c3e50' }} align="center">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoices.map((row: InvoiceRow, i) => (
                <TableRow
                  key={row.id ?? row.code ?? i}
                  sx={{ '&:hover': { backgroundColor: 'rgba(66,119,185,0.03)' } }}>
                  <TableCell>
                    {row.status === 'paid' ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircleIcon color="success" fontSize="small" />
                        <Typography component="span" sx={{ fontWeight: 500, color: '#27ae60' }}>
                          Payée
                        </Typography>
                      </Box>
                    ) : (
                      <Typography sx={{ color: '#7f8c8d' }}>{row.status ?? '—'}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {row.period_start && row.period_end ? (
                      <Typography sx={{ fontWeight: 500 }}>
                        {format(new Date(row.period_start * 1000), 'dd MMM', { locale: fr })} –{' '}
                        {format(new Date(row.period_end * 1000), 'dd MMM yyyy', { locale: fr })}
                      </Typography>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Typography sx={{ fontWeight: 600 }}>
                      {(row.amount_paid ?? 0) / 100} {(row.currency ?? 'eur').toUpperCase()}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                      {row.hosted_invoice_url && (
                        <Link
                          href={row.hosted_invoice_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ fontWeight: 600, textDecoration: 'none' }}>
                          Consulter
                        </Link>
                      )}
                      {row.invoice_pdf && (
                        <Link
                          href={row.invoice_pdf}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ fontWeight: 600, textDecoration: 'none' }}>
                          Télécharger
                        </Link>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  )
}
